import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  Download,
  DollarSign,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Star,
  Send,
  Building,
  User,
  CheckSquare,
  Square,
  Zap,
  Loader2
} from 'lucide-react';
import { quoteAPI } from '../../services/api';
import { useNotificationHook } from '../../hooks/useNotificationHook';

const QuotesManagement = () => {
  const { notify } = useNotificationHook();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterProjectType, setFilterProjectType] = useState('all');
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [quotesPerPage] = useState(10);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    draft: 0,
    sent: 0,
    accepted: 0,
    rejected: 0,
    expired: 0
  });

  // Bulk actions state
  const [selectedQuotes, setSelectedQuotes] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Fetch quotes from backend
  const fetchQuotes = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('🔍 Fetching quotes with params:', { 
        page: currentPage, 
        limit: quotesPerPage,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        projectType: filterProjectType !== 'all' ? filterProjectType : undefined,
        search: searchTerm || undefined
      });
      
      const res = await quoteAPI.getAll({ 
        page: currentPage, 
        limit: quotesPerPage,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        projectType: filterProjectType !== 'all' ? filterProjectType : undefined,
        search: searchTerm || undefined
      });
      setQuotes(res.data.data.quotes || []);
    } catch (err) {
      console.error('❌ Error fetching quotes:', err);
      setError(`Failed to load quotes: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      console.log('🔍 Fetching quote stats...');
      const res = await quoteAPI.getStats();
      console.log('✅ Stats response:', res.data);
      setStats(res.data.data);
    } catch (err) {
      console.error('❌ Failed to load stats:', err);
      // Don't show error for stats, just log it
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchQuotes();
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchQuotes();
    fetchStats();
    // eslint-disable-next-line
  }, [currentPage, filterStatus, filterProjectType]);

  const handleStatusUpdate = async (quoteId, newStatus) => {
    try {
      setLoading(true);
      setError(null);
      const response = await quoteAPI.update(quoteId, { status: newStatus });
      if (response.data && response.data.success) {
        await fetchQuotes();
        await fetchStats();
      } else {
        setError('Failed to update status.');
      }
    } catch (err) {
      console.error('Status update error:', err);
      setError('Failed to update status: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (window.confirm('Are you sure you want to delete this quote?')) {
      try {
        setLoading(true);
        await quoteAPI.delete(quoteId);
        setQuotes(quotes.filter(quote => quote.id !== quoteId));
        await fetchStats();
      } catch (err) {
        setError('Failed to delete quote.');
      } finally {
        setLoading(false);
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', text: 'Draft' },
      sent: { color: 'bg-blue-100 text-blue-800', text: 'Sent' },
      accepted: { color: 'bg-green-100 text-green-800', text: 'Accepted' },
      rejected: { color: 'bg-red-100 text-red-800', text: 'Rejected' },
      expired: { color: 'bg-yellow-100 text-yellow-800', text: 'Expired' }
    };
    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft':
        return <FileText className="w-4 h-4" />;
      case 'sent':
        return <Send className="w-4 h-4" />;
      case 'accepted':
        return <CheckCircle className="w-4 h-4" />;
      case 'rejected':
        return <XCircle className="w-4 h-4" />;
      case 'expired':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Bulk action functions
  const handleSelectQuote = (quoteId) => {
    setSelectedQuotes(prev => 
      prev.includes(quoteId) 
        ? prev.filter(id => id !== quoteId)
        : [...prev, quoteId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedQuotes([]);
      setSelectAll(false);
    } else {
      setSelectedQuotes(quotes.map(quote => quote.id));
      setSelectAll(true);
    }
  };

  const handleBulkStatusUpdate = async (status) => {
    if (selectedQuotes.length === 0) return;
    
    try {
      setBulkActionLoading(true);
      const response = await quoteAPI.bulkUpdateStatus(selectedQuotes, status);
      
      if (response.data.success) {
        notify.success(`Successfully updated ${selectedQuotes.length} quotes to ${status} status`);
        setSelectedQuotes([]);
        setSelectAll(false);
        await fetchQuotes();
        await fetchStats();
      } else {
        notify.error('Failed to update quotes: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating quotes:', error);
      notify.error('Error updating quotes: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedQuotes.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedQuotes.length} quotes? This action cannot be undone.`)) {
      return;
    }
    
    try {
      setBulkActionLoading(true);
      const response = await quoteAPI.bulkDelete(selectedQuotes);
      
      if (response.data.success) {
        notify.success(`Successfully deleted ${selectedQuotes.length} quotes`);
        setSelectedQuotes([]);
        setSelectAll(false);
        await fetchQuotes();
        await fetchStats();
      } else {
        notify.error('Failed to delete quotes: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting quotes:', error);
      notify.error('Error deleting quotes: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Control bulk actions bar visibility
  useEffect(() => {
    setShowBulkActions(selectedQuotes.length > 0);
  }, [selectedQuotes]);

  if (loading && quotes.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
             <div className="mb-6 flex justify-between items-center">
         <div>
           <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Quotes Management</h1>
           <p className="text-gray-600 dark:text-gray-400 mt-1">Manage all quote requests and submissions</p>
         </div>
                   <div className="flex gap-2">
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200"
            >
              <Plus className="w-4 h-4" />
              Add Quote
            </button>
                  </div>
      </div>

      {/* Bulk Actions */}
      {showBulkActions && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 rounded-lg mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                {selectedQuotes.length} quote{selectedQuotes.length !== 1 ? 's' : ''} selected
              </span>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleBulkStatusUpdate('draft')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-gray-600 hover:bg-gray-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileText className="h-3 w-3 mr-1" />}
                  Mark Draft
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate('sent')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
                  Mark Sent
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate('accepted')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                  Mark Accepted
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate('rejected')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <XCircle className="h-3 w-3 mr-1" />}
                  Mark Rejected
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate('expired')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-yellow-600 hover:bg-yellow-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <AlertCircle className="h-3 w-3 mr-1" />}
                  Mark Expired
                </button>
                
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <FileText className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Draft</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{stats.draft}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
              <Send className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Sent</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{stats.sent}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Accepted</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{stats.accepted}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg">
              <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Rejected</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{stats.rejected}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg">
              <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Expired</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{stats.expired}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search quotes..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
              <option value="expired">Expired</option>
            </select>
            <select
              value={filterProjectType}
              onChange={(e) => setFilterProjectType(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Types</option>
              <option value="website">Website</option>
              <option value="webapp">Web App</option>
              <option value="mobile">Mobile App</option>
              <option value="ecommerce">E-commerce</option>
              <option value="design">Design</option>
            </select>
          </div>
        </div>
      </div>

      {/* Quotes Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  <button
                    onClick={handleSelectAll}
                    className="flex items-center"
                  >
                    {selectAll ? (
                      <CheckSquare className="h-4 w-4 text-blue-600" />
                    ) : (
                      <Square className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Quote Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Project Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {quotes.map((quote) => {
                let contactInfo = {};
                let features = [];
                
                try {
                  if (quote.contact_info) {
                    contactInfo = typeof quote.contact_info === 'string' 
                      ? JSON.parse(quote.contact_info) 
                      : quote.contact_info;
                  }
                } catch (error) {
                  console.error('Error parsing contact_info:', error);
                  contactInfo = {};
                }
                
                try {
                  if (quote.features) {
                    features = typeof quote.features === 'string' 
                      ? JSON.parse(quote.features) 
                      : quote.features;
                  }
                } catch (error) {
                  console.error('Error parsing features:', error);
                  features = [];
                }
                return (
                  <tr key={quote.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleSelectQuote(quote.id)}
                        className="flex items-center"
                      >
                        {selectedQuotes.includes(quote.id) ? (
                          <CheckSquare className="h-4 w-4 text-blue-600" />
                        ) : (
                          <Square className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {contactInfo.name || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {contactInfo.email || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {contactInfo.phone || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white capitalize">
                        {quote.project_type}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {features.length} features
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {formatCurrency(quote.estimated_cost)}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {quote.estimated_time}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(quote.status)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {formatDate(quote.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedQuote(quote);
                            setShowViewModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="View"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedQuote(quote);
                            setShowEditModal(true);
                          }}
                          className="text-green-600 hover:text-green-900 p-1 rounded"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <select
                          value={quote.status}
                          onChange={(e) => handleStatusUpdate(quote.id, e.target.value)}
                          className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="draft">Draft</option>
                          <option value="sent">Sent</option>
                          <option value="accepted">Accepted</option>
                          <option value="rejected">Rejected</option>
                          <option value="expired">Expired</option>
                        </select>
                        <button
                          onClick={() => handleDeleteQuote(quote.id)}
                          className="text-red-600 hover:text-red-900 p-1 rounded"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {quotes.length === 0 && !loading && (
        <div className="text-center py-8">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No quotes found</h3>
          <p className="text-gray-500 dark:text-gray-400">No quotes match your current filters.</p>
        </div>
      )}

      {error && (
        <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

             {/* Quote View Modal */}
       {showViewModal && selectedQuote && (
         <QuoteViewModal 
           isOpen={showViewModal} 
           onClose={() => setShowViewModal(false)} 
           quote={selectedQuote} 
         />
       )}

       {/* Add Quote Modal */}
       {showAddModal && (
         <AddQuoteModal 
           isOpen={showAddModal} 
           onClose={() => setShowAddModal(false)} 
           onSuccess={() => {
             setShowAddModal(false);
             fetchQuotes();
             fetchStats();
           }} 
         />
       )}

       {/* Edit Quote Modal */}
       {showEditModal && selectedQuote && (
         <EditQuoteModal 
           isOpen={showEditModal} 
           onClose={() => setShowEditModal(false)} 
           quote={selectedQuote}
           onSuccess={() => {
             setShowEditModal(false);
             setSelectedQuote(null);
             fetchQuotes();
             fetchStats();
           }} 
         />
       )}
     </div>
   );
 };

const QuoteViewModal = ({ isOpen, onClose, quote }) => {
  const { notify } = useNotificationHook();
  if (!isOpen) return null;

  let contactInfo = {};
  let features = [];
  
  try {
    if (quote.contact_info) {
      contactInfo = typeof quote.contact_info === 'string' 
        ? JSON.parse(quote.contact_info) 
        : quote.contact_info;
    }
  } catch (error) {
    console.error('Error parsing contact_info in modal:', error);
    contactInfo = {};
  }
  
  try {
    if (quote.features) {
      features = typeof quote.features === 'string' 
        ? JSON.parse(quote.features) 
        : quote.features;
    }
  } catch (error) {
    console.error('Error parsing features in modal:', error);
    features = [];
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Quote Details</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Contact Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                  <p className="text-sm text-gray-900 dark:text-white">{contactInfo.name || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                  <p className="text-sm text-gray-900 dark:text-white">{contactInfo.email || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone</label>
                  <p className="text-sm text-gray-900 dark:text-white">{contactInfo.phone || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Company</label>
                  <p className="text-sm text-gray-900 dark:text-white">{contactInfo.company || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Project Details */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Project Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Project Type</label>
                  <p className="text-sm text-gray-900 dark:text-white capitalize">{quote.project_type}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Timeline</label>
                  <p className="text-sm text-gray-900 dark:text-white">{quote.timeline}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Complexity</label>
                  <p className="text-sm text-gray-900 dark:text-white capitalize">{quote.complexity}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                  <p className="text-sm text-gray-900 dark:text-white capitalize">{quote.status}</p>
                </div>
              </div>
            </div>

            {/* Features */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Selected Features</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                    <span className="text-sm text-gray-900 dark:text-white capitalize">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Cost Estimate */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Cost Estimate</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Estimated Cost</label>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    ₹{quote.estimated_cost.toLocaleString()}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Estimated Time</label>
                  <p className="text-sm text-gray-900 dark:text-white">{quote.estimated_time}</p>
                </div>
              </div>
            </div>

            {/* Dates */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Important Dates</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Created</label>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {new Date(quote.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Valid Until</label>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {quote.valid_until ? new Date(quote.valid_until).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-6 space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              Close
            </button>
                         <button
               onClick={async () => {
                 try {
                   const response = await quoteAPI.downloadPDF(quote.id);
                   if (response.data) {
                     const blob = response.data;
                     const url = window.URL.createObjectURL(blob);
                     const link = document.createElement('a');
                     link.href = url;
                     link.download = `quote-${quote.id}-${Date.now()}.pdf`;
                     document.body.appendChild(link);
                     link.click();
                     document.body.removeChild(link);
                     window.URL.revokeObjectURL(url);
                   }
                 } catch (error) {
                   console.error('Error downloading PDF:', error);
                   notify.error('Failed to download PDF. Please try again.');
                 }
               }}
               className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
             >
               Download PDF
             </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AddQuoteModal = ({ isOpen, onClose, onSuccess }) => {
  const { notify } = useNotificationHook();
  const [formData, setFormData] = useState({
    projectType: 'website',
    features: [''],
    timeline: 'standard',
    complexity: 'medium',
    contact: {
      name: '',
      email: '',
      phone: '',
      company: ''
    }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.contact.name.trim() || !formData.contact.email.trim()) {
      notify.error('Please fill in the contact name and email fields.');
      return;
    }
    
    // Clean up features array - remove empty strings
    const cleanedFormData = {
      ...formData,
      features: formData.features.filter(feature => feature.trim() !== '')
    };
    
    console.log('🔍 Submitting quote with data:', cleanedFormData);
    
    try {
      const response = await quoteAPI.create(cleanedFormData);
      console.log('✅ Quote created successfully:', response.data);
      onSuccess();
      onClose(); // Close the modal after successful creation
    } catch (error) {
      console.error('❌ Error creating quote:', error);
      console.error('❌ Error response:', error.response?.data);
      notify.error('Failed to create quote: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Add New Quote</h2>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Project Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Project Type
              </label>
              <select
                value={formData.projectType}
                onChange={(e) => handleInputChange('projectType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                required
              >
                <option value="website">Website</option>
                <option value="webapp">Web Application</option>
                <option value="mobile">Mobile App</option>
                <option value="ecommerce">E-commerce</option>
                <option value="design">Design</option>
              </select>
            </div>

            {/* Timeline */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Timeline
              </label>
              <select
                value={formData.timeline}
                onChange={(e) => handleInputChange('timeline', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                required
              >
                <option value="rush">Rush (1-2 weeks)</option>
                <option value="standard">Standard (3-4 weeks)</option>
                <option value="extended">Extended (6-8 weeks)</option>
              </select>
            </div>

            {/* Complexity */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Complexity
              </label>
              <select
                value={formData.complexity}
                onChange={(e) => handleInputChange('complexity', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                required
              >
                <option value="simple">Simple</option>
                <option value="medium">Medium</option>
                <option value="complex">Complex</option>
              </select>
            </div>

            {/* Features */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Features
              </label>
              <div className="space-y-2">
                {formData.features.map((feature, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => {
                        const newFeatures = [...formData.features];
                        newFeatures[index] = e.target.value;
                        handleInputChange('features', newFeatures);
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Enter feature"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newFeatures = formData.features.filter((_, i) => i !== index);
                        handleInputChange('features', newFeatures);
                      }}
                      className="px-3 py-2 text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    const newFeatures = [...formData.features, ''];
                    handleInputChange('features', newFeatures);
                  }}
                  className="px-3 py-2 text-blue-600 hover:text-blue-800"
                >
                  + Add Feature
                </button>
              </div>
            </div>

            {/* Contact Information */}
            <div className="border-t pt-4">
              <h3 className="text-lg font-medium mb-3">Contact Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.contact.name}
                    onChange={(e) => handleInputChange('contact.name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.contact.email}
                    onChange={(e) => handleInputChange('contact.email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.contact.phone}
                    onChange={(e) => handleInputChange('contact.phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Company
                  </label>
                  <input
                    type="text"
                    value={formData.contact.company}
                    onChange={(e) => handleInputChange('contact.company', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-2 mt-6">
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
            >
              Create Quote
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditQuoteModal = ({ isOpen, onClose, quote, onSuccess }) => {
  const { notify } = useNotificationHook();
  const [formData, setFormData] = useState({
    projectType: quote?.project_type || 'website',
    features: quote?.features || [],
    timeline: quote?.timeline || 'standard',
    complexity: quote?.complexity || 'medium',
    status: quote?.status || 'draft',
    estimatedCost: quote?.estimated_cost || '',
    estimatedTime: quote?.estimated_time || '',
    validUntil: quote?.valid_until || '',
    notes: quote?.notes || '',
    contact: quote?.contact_info || {
      name: '',
      email: '',
      phone: '',
      company: ''
    }
  });

  useEffect(() => {
    if (quote) {
      let contactInfo = {};
      let features = [];
      
      try {
        if (quote.contact_info) {
          contactInfo = typeof quote.contact_info === 'string' 
            ? JSON.parse(quote.contact_info) 
            : quote.contact_info;
        }
      } catch (error) {
        console.error('Error parsing contact_info:', error);
        contactInfo = {};
      }
      
      try {
        if (quote.features) {
          features = typeof quote.features === 'string' 
            ? JSON.parse(quote.features) 
            : quote.features;
        }
      } catch (error) {
        console.error('Error parsing features:', error);
        features = [];
      }

      setFormData({
        projectType: quote.project_type || 'website',
        features: features,
        timeline: quote.timeline || 'standard',
        complexity: quote.complexity || 'medium',
        status: quote.status || 'draft',
        estimatedCost: quote.estimated_cost || '',
        estimatedTime: quote.estimated_time || '',
        validUntil: quote.valid_until || '',
        notes: quote.notes || '',
        contact: contactInfo
      });
    }
  }, [quote]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Map form data to backend field names
      const updateData = {
        projectType: formData.projectType,
        features: formData.features,
        timeline: formData.timeline,
        complexity: formData.complexity,
        status: formData.status,
        estimatedCost: formData.estimatedCost,
        estimatedTime: formData.estimatedTime,
        validUntil: formData.validUntil,
        notes: formData.notes,
        contact: formData.contact
      };
      
      await quoteAPI.update(quote.id, updateData);
      onSuccess();
    } catch (error) {
      console.error('Error updating quote:', error);
      notify.error('Failed to update quote: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData(prev => ({
      ...prev,
      features: newFeatures
    }));
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const removeFeature = (index) => {
    if (window.confirm('Are you sure you want to remove this feature? This action cannot be undone.')) {
      setFormData(prev => ({
        ...prev,
        features: prev.features.filter((_, i) => i !== index)
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Edit Quote</h2>
        <form onSubmit={handleSubmit}>
          {/* Project Details */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4">Project Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Project Type
                </label>
                <select
                  value={formData.projectType}
                  onChange={(e) => handleInputChange('projectType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="website">Website</option>
                  <option value="webapp">Web App</option>
                  <option value="mobile">Mobile App</option>
                  <option value="ecommerce">E-commerce</option>
                  <option value="design">Design</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Timeline
                </label>
                <select
                  value={formData.timeline}
                  onChange={(e) => handleInputChange('timeline', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="rush">Rush</option>
                  <option value="standard">Standard</option>
                  <option value="extended">Extended</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Complexity
                </label>
                <select
                  value={formData.complexity}
                  onChange={(e) => handleInputChange('complexity', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="simple">Simple</option>
                  <option value="medium">Medium</option>
                  <option value="complex">Complex</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Estimated Cost (₹)
                </label>
                <input
                  type="number"
                  value={formData.estimatedCost}
                  onChange={(e) => handleInputChange('estimatedCost', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="50000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Estimated Time
                </label>
                <input
                  type="text"
                  value={formData.estimatedTime}
                  onChange={(e) => handleInputChange('estimatedTime', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="4-6 weeks"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Valid Until
                </label>
                <input
                  type="date"
                  value={formData.validUntil ? formData.validUntil.split('T')[0] : ''}
                  onChange={(e) => handleInputChange('validUntil', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Features</h3>
              <button
                type="button"
                onClick={addFeature}
                className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
              >
                Add Feature
              </button>
            </div>
            <div className="space-y-2">
              {formData.features.map((feature, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={feature}
                    onChange={(e) => handleFeatureChange(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Enter feature description"
                  />
                  <button
                    type="button"
                    onClick={() => removeFeature(index)}
                    className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Remove
                  </button>
                </div>
              ))}
              {formData.features.length === 0 && (
                <p className="text-gray-500 text-sm">No features added yet. Click "Add Feature" to add one.</p>
              )}
            </div>
          </div>

          {/* Contact Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Name"
                value={formData.contact.name || ''}
                onChange={(e) => handleInputChange('contact.name', e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
              <input
                type="email"
                placeholder="Email"
                value={formData.contact.email || ''}
                onChange={(e) => handleInputChange('contact.email', e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
              <input
                type="text"
                placeholder="Phone"
                value={formData.contact.phone || ''}
                onChange={(e) => handleInputChange('contact.phone', e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
              <input
                type="text"
                placeholder="Company"
                value={formData.contact.company || ''}
                onChange={(e) => handleInputChange('contact.company', e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          {/* Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="Add any additional notes about this quote..."
            />
          </div>

          <div className="flex gap-2 mt-6">
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
            >
              Update Quote
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuotesManagement; 